import CandidatesUserJobProcessScore from './CandidatesUserJobProcessScore';
import clsx from 'clsx';
import { MaterialIcon } from '@/components/MaterialIcon';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { CandidateProfileResponse } from '../types/CandidateProfile';
import { DRAWER_TAB_ITEMS } from '../constants';
import CandidatesBoardsMenu from './CandidatesBoardsMenu';
import type { AgentApiCandidateProfilesBoard } from '@/types/serializers';
import UsersMenu from '@/features/agent/components/UsersMenu';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import useUpdateCandidateProfile from '../api/useUpdateCandidateProfile';
import useFetchIam from '@/features/agent/api/useFetchIam';
import { showErrorToast } from '../../job_change_processes/lib/toast';

type Props = {
  candidateId: number;
  tab: (typeof DRAWER_TAB_ITEMS)[number]['value'];
  candidateName: string;
  organizationUserIds: number[];
  files: {
    id: number;
    filename: string;
    url: string;
  }[];
  companyName: string | null;
  jobPosition: string | null;
  age: string;
  scores: {
    suggest: number;
    documentScreening: number;
    interview: number;
    offer: number;
    close: number;
  };
  cacandidateBoards: AgentApiCandidateProfilesBoard[];
  candidateBoard?: {
    id?: number;
    title?: string;
    colorType?: 'gray' | 'red' | 'purple' | 'blue' | 'yellow' | 'green';
  };
  setTab: (val: (typeof DRAWER_TAB_ITEMS)[number]['value']) => void;
  drawerClose: () => void;
  candidateProfileRefetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<CandidateProfileResponse, Error>>;
  onBoardChanged?: () => void;
};

export default function CandidatesProfileDrawerTab({
  candidateId,
  tab,
  setTab,
  candidateName,
  organizationUserIds,
  files,
  companyName,
  jobPosition,
  age,
  scores,
  cacandidateBoards,
  candidateBoard,
  drawerClose,
  candidateProfileRefetch,
  onBoardChanged,
}: Props) {
  const { data: activeUsersData } = useActiveUsers();
  const activeUsers = activeUsersData?.activeUsers ?? [];
  const { data: iam } = useFetchIam();
  const { mutateAsync: updateCandidate } = useUpdateCandidateProfile();

  const handleSelectAssignees = async (userIds: number[]) => {
    try {
      await updateCandidate({
        candidateProfileId: candidateId,
        organization_user_ids: userIds,
      });
      await candidateProfileRefetch();
    } catch (e) {
      showErrorToast('担当者の更新に失敗しました');
    }
  };

  return (
    <div className="relative z-30 bg-white shadow-[0px_0px_5px_0px_#00000040]">
      <div className="relative ml-auto flex h-xxl items-center justify-end px-3">
        <button
          type="button"
          className={clsx('flex size-[36px] cursor-pointer items-center justify-center')}
          onClick={(e) => {
            e.stopPropagation();
            drawerClose();
          }}
        >
          <img src="/agent/candidates/icon-arrow-down.svg" width={24} height={24} alt="" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 px-8">
        <div className="flex max-w-[518px] min-w-[320px] flex-1 flex-col gap-4">
          <div className="flex items-center gap-4">
            <p className="text-xl font-bold whitespace-nowrap">{candidateName}</p>
            <UsersMenu
              activeUsers={activeUsers}
              selectedUserIds={organizationUserIds}
              onSelect={handleSelectAssignees}
              currentUserId={iam?.current_user?.id}
            />
            <CandidatesBoardsMenu
              candidatesBoards={cacandidateBoards}
              candidateProfileId={candidateId}
              candidateProfileRefetch={candidateProfileRefetch}
              candidateBoard={candidateBoard}
              onBoardChanged={onBoardChanged}
            />
            {files.length > 0 && (
              <div className="ml-auto flex items-center gap-3">
                {files.map((f) => (
                  <div key={f.id} className="group relative">
                    <button
                      type="button"
                      className="flex size-xl items-center justify-center rounded border border-solid border-border bg-white hover:bg-tag-gray"
                      onClick={() => window.open(f.url, '_blank')}
                    >
                      <img src="/agent/candidates/icon-file.svg" alt="" width={16} height={16} />
                    </button>
                    <div className="absolute top-full left-1/2 z-10 mt-2 hidden w-max -translate-x-1/2 transform rounded bg-black p-2 text-xs text-white group-hover:block">
                      {f.filename}
                      <div className="absolute -top-2 left-1/2 size-0 -translate-x-1/2 border-4 border-solid border-transparent border-b-black"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-6">
            {companyName && (
              <div className="flex items-center gap-2">
                <MaterialIcon name="apartment" filled className="text-text/placeholder text-xl" />
                <p className="text-xs">{companyName}</p>
              </div>
            )}
            {jobPosition && (
              <div className="flex items-center gap-2">
                <MaterialIcon name="work" filled className="text-text/placeholder text-xl" />
                <p className="text-xs">{jobPosition}</p>
              </div>
            )}
            {age && (
              <div className="flex items-center gap-2">
                <MaterialIcon name="person" filled className="text-text/placeholder text-xl" />
                <p className="text-xs">{age}歳</p>
              </div>
            )}
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-4">
          <CandidatesUserJobProcessScore text="提案" score={scores.suggest} />
          <CandidatesUserJobProcessScore text="書類選考" score={scores.documentScreening} />
          <CandidatesUserJobProcessScore text="選考中" score={scores.interview} />
          <CandidatesUserJobProcessScore text="内定" score={scores.offer} />
          <CandidatesUserJobProcessScore text="内定承諾" score={scores.close} />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-around">
        {DRAWER_TAB_ITEMS.map((item) => (
          <button
            type="button"
            key={item.value}
            className={clsx(
              'h-[44px] w-full cursor-pointer text-sm hover:font-bold hover:text-primary',
              tab === item.value &&
                'border-0 border-b-2 border-solid border-b-primary font-bold text-primary'
            )}
            onClick={() => setTab(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
